import { useState } from 'react';
import Alert from '@v2/shared/components/Alert';
import Breadcrumbs from '@v2/shared/components/Breadcrumbs';
import Button from '@v2/shared/components/Button';
import ButtonLink from '@v2/shared/components/ButtonLink';
import { FormInputPhone } from '@v2/shared/components/Form';
import { useSite } from '@v2/shared/lib/site';
import useUrlGenerator from '@v2/shared/lib/useUrlGenerator';
import { Props } from './types';

/**
 * A centred column rather than the page's full width. This is a landing page
 * with one thing on it, not an account page with a menu down the side, so the
 * question sits in the middle of the screen where somebody arriving from an
 * email is already looking.
 */
const COLUMN = 'max-w-2xl mx-auto';

/**
 * The pill an anchor has to borrow, because these two destinations are real
 * navigations and `Button` renders a `<button>`. `ButtonLink` merges rather than
 * concatenates, so each of these replaces the default it collides with.
 */
const PILL = 'rounded-full px-8 py-2 text-lg normal-case tracking-normal';
const PILL_SECONDARY = `${PILL} border-2 border-slate-900 bg-white text-slate-900 hover:bg-slate-900 hover:text-white`;

/** Matches SmsOptInPagePropsBuilder::CSRF_FIELD, which is what the controller reads. */
const CSRF_FIELD = '_csrf_token';

/**
 * /my-account/{discountlock}/{id}/sms_optin — one question, asked once. The
 * {id} is the subscription, the same one the manage page's url carries.
 *
 * The page an invitation email lands on, so it opens with the answer already
 * half filled in: the customer's magazine, their renewal date and the number we
 * hold for them, and a single button. Everything that makes the preferences page
 * a form — a checkbox per list, an unsubscribe link, a save that reports per-list
 * outcomes — is deliberately absent. This is not where a customer manages their
 * texts; the success state links to the page that is.
 *
 * It posts to itself rather than to the storefront api, and renders on the
 * server rather than fetching the way the manage page next door fetches. Both
 * are the same decision: somebody arriving from an email should find the
 * question on the screen and be able to answer it, without a round trip to wait
 * for and without javascript — an email client's in-app browser is where this
 * will mostly be opened.
 *
 * The number is drawn in the same masked input the preferences page uses, with
 * no masking of the value, no "use a different number" link and no second state
 * to toggle into: the customer sees the number and can change it right there. A
 * customer we hold no number for gets an empty field, which is not a special
 * case — it is the same field with nothing in it.
 */
export default function DiscountLockSmsOptIn({
  title,
  renewalDate,
  serviceName,
  telephone: initialTelephone,
  subscribed,
  error,
  formAction,
  csrfToken,
  accountUrl,
  manageUrl,
  phonePreferencesUrl,
}: Props) {
  // The brand the texts come from, off the storefront being rendered rather than
  // written down — see the disclosure below.
  const { name: siteName } = useSite();
  const { buildUrl } = useUrlGenerator();

  // Seeded from the server, not filled in by an effect: this page is server
  // rendered, and a field that arrives empty and populates on hydration is both
  // a flash and a mismatch.
  const [telephone, setTelephone] = useState(initialTelephone);

  const trail = (
    <Breadcrumbs
      items={[
        { label: 'Account', href: accountUrl },
        { label: 'Notifications', href: phonePreferencesUrl },
        { label: 'Renewal reminders' },
      ]}
    />
  );

  if (subscribed) {
    return (
      <div className={COLUMN}>
        {trail}

        <h1 className="font-bold text-2xl md:text-3xl mb-3">You&apos;re all set</h1>

        {/*
          Deliberately does NOT say a confirmation text has been sent. The
          reminders list is single opt-in — the row is the subscription — so
          nothing is sent here, and the mockup's version of this sentence
          described a double opt-in the page does not do.
        */}
        <p className="text-sm md:text-base text-slate-600 mb-5 md:mb-8 font-medium">
          You&apos;ll get a reminder about 30 days and again 7 days before your next renewal
          {title ? ` of ${title}` : ''}
          {renewalDate ? `, on ${renewalDate}` : ''}.
        </p>

        <div className="rounded-xl border border-slate-200 p-5 md:p-8">
          <Alert color="success" className="mb-5">
            Renewal reminder texts are on{title ? ` for ${title}` : ''}.
          </Alert>

          <p className="text-slate-600 mb-6">
            Change your mind anytime: reply STOP to any text, or manage your preferences under
            Account &rsaquo; Notifications &rsaquo; Phone.
          </p>

          <div className="flex flex-wrap gap-3">
            <ButtonLink href={buildUrl(manageUrl)} className={PILL}>
              Manage {serviceName}
            </ButtonLink>

            <ButtonLink href={buildUrl(phonePreferencesUrl)} className={PILL_SECONDARY}>
              Phone preferences
            </ButtonLink>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className={COLUMN}>
      {trail}

      <h1 className="font-bold text-2xl md:text-3xl mb-3">Get a text before your renewal</h1>

      <p className="text-sm md:text-base text-slate-600 mb-5 md:mb-8 font-medium">
        We&apos;ll text you before each {serviceName} renewal charge, with the date, price, and a
        link to keep, delay, or turn off the renewal.
      </p>

      <form method="post" action={formAction}>
        {/*
          The same token every other account form carries — this one posts to a
          page behind a login wall that writes against the session's customer.
          See EmailPreferences, which posts to customer.php the same way.
        */}
        <input type="hidden" name={CSRF_FIELD} value={csrfToken} />

        <div className="rounded-xl border border-slate-200 p-5 md:p-8">
          {title && <p className="font-bold text-xl mb-1 mt-0">{title}</p>}

          <p className="text-slate-600 mt-0 mb-5">
            {serviceName} auto-renewal
            {renewalDate ? ` · next renewal ${renewalDate}` : ''}
          </p>

          <hr className="border-slate-200 mb-6" />

          {error && (
            <Alert color="danger" className="mb-5">
              {error}
            </Alert>
          )}

          <FormInputPhone
            id="telephone"
            name="telephone"
            label="We'll text this number"
            value={telephone}
            onChange={setTelephone}
            placeholder="(___) ___-____"
          />

          <p className="text-sm text-slate-600 -mt-3 mb-6">US and Canadian mobile numbers only.</p>

          <Button type="submit" color="primary" fullWidth>
            Yes, text me renewal reminders
          </Button>

          {/*
            The consent language a carrier reads, and the reason it sits directly
            under the button rather than anywhere tidier: it describes that
            button, and a disclosure a customer has to scroll to find is one they
            did not agree to. Registered with the carriers as part of the 10DLC
            campaign DiscountLock-Renewal-Reminders, so the wording is pinned by
            SmsConsentDisclosureTest rather than left to a copy pass.

            It never says "Reply STOP to cancel" and must not learn to: customers
            read it as cancelling the subscription itself, which is the opposite
            of what the sentence is offering.

            The brand, the service and both links come off the storefront being
            rendered rather than out of this file. Nothing under shared/ names a
            brand — this page is mounted by whichever site runs the programme, and
            a notice naming DiscountMags on MagSpot would be telling the customer
            they had agreed with somebody else.
          */}
          <p className="text-sm text-slate-600 mt-4 mb-0">
            By tapping &ldquo;Yes, text me renewal reminders&rdquo; you agree to receive
            transactional {serviceName} renewal reminder and account texts from {siteName} at the
            number above. Consent is not a condition of purchase. Message frequency varies. Msg
            &amp; data rates may apply. Reply HELP for help, STOP to opt out at any time.{' '}
            <a
              href={buildUrl('/privacy')}
              target="_blank"
              rel="noreferrer"
              className="underline font-medium"
            >
              Privacy Policy
            </a>{' '}
            &middot;{' '}
            <a
              href={buildUrl('/terms')}
              target="_blank"
              rel="noreferrer"
              className="underline font-medium"
            >
              Terms of Use
            </a>
          </p>
        </div>
      </form>

      <p className="text-center mt-6">
        <a href={buildUrl(manageUrl)} className="text-slate-600 underline">
          No thanks, keep email reminders only
        </a>
      </p>
    </div>
  );
}

DiscountLockSmsOptIn.layoutName = 'Layouts.Full';
